import React from "react";
import {
  Autorenew as AutorenewIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Pending as PendingIcon,
} from "@mui/icons-material";

import {
  TTSTagStatusDBEnum,
  TTSTagStatusEnum,
  TTS_DEBUG,
} from "../../providers/TTSFileStatusProvider";

const SPIN_KEYFRAMES = {
  "0%": { transform: "rotate(0deg)" },
  "100%": { transform: "rotate(360deg)" },
};

const ICON_COLORS = Object.freeze({
  ERROR: "#f70404",
  PENDING: "#FFC107",
  PROCESSING: "#00ACC1",
});

export const TTS_ICON_TYPE = Object.freeze({
  PROCESSING: "processing",
  PENDING: "pending",
  ERROR: "error",
  CHECK: "check",
});

// Decide which icon (if any) to render for the TTS tab. Pulled out as a pure
// function so the precedence rules are testable without mounting MUI.
//
// Precedence (must match the original inline order in Item/PassageContentCard):
//   1. No TTS tags → null
//   2. Live status PROCESSING → spin
//   3. DB tags out of sync (PENDING) → pending dot
//   4. Live status FAILED → red error
//   5. Retryable DB tags → red error
//   6. DB tags still processing/enqueued → spin
//   7. Live GENERATED or DB SAVED → green check
//   8. Otherwise → null
export const selectTTSIconType = ({
  hasTTSTags,
  ttsTagStatus,
  ttsObjectStatus,
  retryable,
  processingDb,
}) => {
  if (!hasTTSTags) return null;
  if (ttsTagStatus === TTSTagStatusEnum.PROCESSING)
    return TTS_ICON_TYPE.PROCESSING;
  if (ttsObjectStatus === TTSTagStatusDBEnum.PENDING)
    return TTS_ICON_TYPE.PENDING;
  if (ttsTagStatus === TTSTagStatusEnum.FAILED) return TTS_ICON_TYPE.ERROR;
  if (retryable) return TTS_ICON_TYPE.ERROR;
  if (processingDb) return TTS_ICON_TYPE.PROCESSING;
  if (
    ttsTagStatus === TTSTagStatusEnum.GENERATED ||
    ttsObjectStatus === TTSTagStatusDBEnum.SAVED
  )
    return TTS_ICON_TYPE.CHECK;
  return null;
};

const TTSStatusIcon = ({ type, debugContext }) => {
  if (TTS_DEBUG && debugContext) {
    console.info(`[TTS Icon - ${debugContext}] → ${type ?? "null"}`);
  }
  if (!type) return null;

  switch (type) {
    case TTS_ICON_TYPE.PROCESSING:
      return (
        <AutorenewIcon
          fontSize="small"
          sx={{
            color: ICON_COLORS.PROCESSING,
            ml: 1,
            animation: "spin 1s linear infinite",
            "@keyframes spin": SPIN_KEYFRAMES,
          }}
        />
      );
    case TTS_ICON_TYPE.PENDING:
      return (
        <PendingIcon
          fontSize="small"
          sx={{ color: ICON_COLORS.PENDING, ml: 1 }}
        />
      );
    case TTS_ICON_TYPE.ERROR:
      return (
        <ErrorIcon fontSize="small" sx={{ color: ICON_COLORS.ERROR, ml: 1 }} />
      );
    case TTS_ICON_TYPE.CHECK:
      return (
        <CheckCircleIcon
          fontSize="small"
          sx={{ color: "success.main", ml: 1 }}
        />
      );
    default:
      return null;
  }
};

export { TTSStatusIcon };
